import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { roundsAPI } from '../../services/api';
import { isCumulativeRound, hasBowl, championLabel } from '../../utils/eventTypes';

// Fixed row height so live rows can slide smoothly (CSS `top` transition) when
// the order changes, instead of snapping/flashing — same as FriDave.
const ROW_HEIGHT = 52;

function BowlIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 48 54" fill="none" aria-label="Bowl" style={{ flexShrink: 0 }}>
      <circle cx="5.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <circle cx="42.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <path d="M7 13 Q24 8 41 13 L36 27 Q24 31 12 27 Z" fill="#C8C8C8" stroke="#A0A0A0" strokeWidth="0.8" strokeLinejoin="round"/>
      <ellipse cx="24" cy="13" rx="17" ry="3.5" fill="#E0E0E0" stroke="#A0A0A0" strokeWidth="0.7"/>
      <rect x="21.5" y="27" width="5" height="5" rx="1" fill="#C8C8C8"/>
      <ellipse cx="24" cy="33" rx="6.5" ry="3" fill="#D0D0D0"/>
      <rect x="14" y="36" width="20" height="3.5" rx="1" fill="#C0C0C0"/>
      <rect x="11" y="39.5" width="26" height="5" rx="2" fill="#B0B0B0"/>
    </svg>
  );
}

export default function Leaderboard({ leaderboard, round, roundId: roundIdProp }) {
  const navigate = useNavigate();
  const { id: paramRoundId } = useParams();
  // roundId is normally read off the route (ActiveRound at /rounds/:id), but a
  // landing page showing the leaderboard directly (no :id in its own route,
  // e.g. ClubhouseLanding) must pass it explicitly.
  const roundId = roundIdProp ?? paramRoundId;
  const location = useLocation();

  // Keep links inside whichever shell we're rendered in, so a guest tapping a
  // player never lands on a TML-branded page.
  const base = location.pathname.startsWith('/clubhouse') ? '/clubhouse' : '';

  const [r1RoundId, setR1RoundId] = useState(null);

  // Show cumulative + current-round columns only when there are prior completed
  // championship rounds — detected by any player having total_points != current_round_points.
  // This avoids relying on round_number (which counts practice rounds too).
  const isCumulative = isCumulativeRound(round);
  const showBowl = hasBowl(round?.event_type);
  const isChampionshipRound2Plus = isCumulative &&
    (leaderboard || []).some(
      p => Number(p.total_points || 0) !== Number(p.current_round_points || 0)
    );

  const isCompleted = round?.status === 'completed';

  // Fetch R1 round id when this is a completed R2 TML championship
  useEffect(() => {
    if (!isCompleted || !isChampionshipRound2Plus || !round?.event_name) return;
    roundsAPI.getAll({ eventName: round.event_name, eventType: round.event_type, roundType: 'championship', status: 'completed' })
      .then(rounds => {
        const others = (rounds || []).filter(r => String(r.id) !== String(roundId));
        if (others.length > 0) {
          // Sort by start_date asc, take first (earliest = R1)
          others.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          setR1RoundId(others[0].id);
        }
      })
      .catch(() => {});
  }, [isCompleted, isChampionshipRound2Plus, round?.event_name, roundId]);

  const leaderboardWithPositions = (leaderboard || []).map((player, index, arr) => {
    let displayPosition = index + 1;
    if (index > 0) {
      const prev = arr[index - 1];
      if (player.total_points === prev.total_points && player.total_strokes === prev.total_strokes) {
        displayPosition = arr[index - 1].displayPosition ?? index;
      }
    }
    return { ...player, displayPosition };
  });

  const bowlChampionId = isCompleted && isChampionshipRound2Plus && showBowl
    ? leaderboardWithPositions.find(p => p.is_bowl)?.player_id
    : null;

  const handlePlayerClick = (playerId) => {
    navigate(roundId ? `${base}/rounds/${roundId}/players/${playerId}` : `${base}/players/${playerId}`);
  };

  const isLive = round?.status === 'in_progress' || round?.status === 'active';

  if (!leaderboard || leaderboard.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mx-4">
        <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200">
          <div className="w-12 shrink-0 text-center">Pos</div>
          <div className="flex-1">Player</div>
          <div className="w-16 shrink-0 text-center">Pts</div>
          <div className="w-16 shrink-0 text-center">Thru</div>
          {isChampionshipRound2Plus && <div className="w-16 shrink-0 text-center">Rnd</div>}
        </div>
        <p className="text-gray-500 text-center py-8 text-sm">No scores yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mx-4">
      {/* Header */}
      <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-semibold text-gray-700 uppercase tracking-wide">
        <div className="w-12 shrink-0 text-center">Pos</div>
        <div className="flex-1">Player</div>
        <div className="w-16 shrink-0 text-center">Pts</div>
        <div className="w-16 shrink-0 text-center">Thru</div>
        {isChampionshipRound2Plus && (
          <div className="w-16 shrink-0 text-center">Rnd</div>
        )}
      </div>

      {/* Rows — live rounds use absolute-positioned rows that SLIDE (CSS top
          transition) so updates don't snap/flash; completed rounds render static
          (they can have taller champion/bowl label rows). */}
      {isLive ? (
        <div style={{ position: 'relative', height: leaderboardWithPositions.length * ROW_HEIGHT }}>
          {leaderboardWithPositions.map((player, index) => {
            const isTied = index > 0 && player.displayPosition === leaderboardWithPositions[index - 1].displayPosition;
            return (
              <button
                key={player.round_player_id}
                onClick={() => handlePlayerClick(player.player_id)}
                className="px-4 flex items-center border-b border-gray-100 bg-white hover:bg-gray-50 text-left"
                style={{
                  position: 'absolute', left: 0, right: 0,
                  top: index * ROW_HEIGHT, height: ROW_HEIGHT,
                  transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <div className="w-12 shrink-0 text-center text-sm font-semibold text-gray-600">
                  {isTied && <span className="text-xs">T</span>}{player.displayPosition}
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
                  <span className="font-bold text-gray-900 truncate" style={{ fontSize: '20px' }}>
                    {player.player_name}
                  </span>
                  {showBowl && player.is_bowl && <BowlIcon />}
                </div>
                <div className="w-16 shrink-0 text-center font-bold text-red-600" style={{ fontSize: '20px' }}>
                  {player.total_points}
                </div>
                <div className="w-16 shrink-0 text-center text-sm text-gray-500">
                  {player.holes_completed}
                </div>
                {isChampionshipRound2Plus && (
                  <div className="w-16 shrink-0 text-center text-sm text-gray-500">
                    {player.current_round_points || 0}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        leaderboardWithPositions.map((player, index) => {
          const isTied = index > 0 && player.displayPosition === leaderboardWithPositions[index - 1].displayPosition;
          const isChampion = isCompleted && isChampionshipRound2Plus && player.displayPosition === 1 && !isTied;
          const isBowlChampion = player.player_id === bowlChampionId;

          return (
            <button
              key={player.round_player_id}
              onClick={() => handlePlayerClick(player.player_id)}
              className="w-full px-4 py-3 flex items-center border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50 text-left"
            >
              {/* Position */}
              <div className="w-12 shrink-0 text-center text-sm font-semibold text-gray-600">
                {isTied && <span className="text-xs">T</span>}{player.displayPosition}
              </div>

              {/* Name + sublabels */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-gray-900 truncate" style={{ fontSize: '20px' }}>
                    {player.player_name}
                  </span>
                  {showBowl && player.is_bowl && <BowlIcon />}
                </div>
                {isChampion && (
                  <p className="text-xs font-semibold text-gray-600 mt-0.5">
                    {championLabel(round?.event_type)}
                    {player.is_playoff_winner ? ' · winner after playoff' : ''}
                  </p>
                )}
                {isBowlChampion && (
                  <p className="text-xs font-semibold text-gray-500 mt-0.5">
                    Winner of the Bowl
                    {player.is_bowl_playoff_winner ? ' · winner after playoff' : ''}
                  </p>
                )}
              </div>

              {/* Total points */}
              <div className="w-16 shrink-0 text-center font-bold text-red-600" style={{ fontSize: '20px' }}>
                {player.total_points}
              </div>

              {/* Thru */}
              <div className="w-16 shrink-0 text-center text-sm text-gray-500">
                {player.holes_completed}
              </div>

              {/* Current round (R2, R3…) */}
              {isChampionshipRound2Plus && (
                <div className="w-16 shrink-0 text-center text-sm text-gray-500">
                  {player.current_round_points || 0}
                </div>
              )}
            </button>
          );
        })
      )}

      {/* R1 results link — shown at bottom of a completed cumulative R2 */}
      {isCompleted && isChampionshipRound2Plus && r1RoundId && (
        <button
          onClick={() => navigate(`${base}/rounds/${r1RoundId}/results`, { state: { backLabel: 'Leaderboard' } })}
          className="w-full px-4 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div>
            <p className="text-sm font-semibold text-gray-700">
              {showBowl ? 'Championship Round 1 results' : 'Round 1 results'}
            </p>
            <p className="text-xs text-gray-400">
              {showBowl ? 'Stableford · Gross · Fivers' : 'Stableford · Gross'}
            </p>
          </div>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}
